import {
  Bone,
  Box3,
  Group,
  Object3D,
  Box3Helper,
  Quaternion,
  Skeleton,
  SkinnedMesh,
  Vector3,
  Vector2,
  Color,
} from "three";
import { vec3 } from "gl-matrix";

import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { Context } from "../../context";

import { Player, teamIdToAbbrev } from "../../../lib/types/statsApi";
import { fetchExtractJson } from "../../../lib/misc/util";
import { calculateAlpha } from "../../util/clock";

import { Skin } from "../../../lib/skin";

import { groundLevel } from "../../../lib/misc/ground";
import { Frames, TaggedNeighborPair } from "../../util/frames";
import { lerpScalar } from "../../../lib/misc/maths";

import { ActorDisc } from "./add-ons/disc";
import { ActorNameplate } from "./add-ons/nameplate";
import { ActorTrack } from "./add-ons/track";
import { TrackLabel } from "./helpers/label";
import { ActorTransitioner } from "./util/transitioner";
import { Material } from "mannequin/pik-solver/src/lib/space/collision";
import {
  ActorPose,
  ActorPoseCompressed,
} from "mannequin/pik-solver/src/apps/mannequin/types/actor-pose";
import { QuaternionPacker } from "mannequin/pik-solver/src/lib/util/quat-pack";
import {
  PoserPlayerType,
  getPoserPlayerType,
} from "../../../lib/misc/player-type";
import { PlayerType as MannequinPlayerType } from "mannequin/src/types/player";

export interface ActorDetails {
  type: PoserPlayerType;
  uid: number;
  actorId: number;
  player?: Player;
}

// TODO: this is a horrendous workaround to support legacy games
function getActorPosePos(
  pose: ActorPose | ActorPoseCompressed
): vec3 | undefined {
  return pose instanceof ActorPose ? pose.rootPose?.pos : pose.rootPos;
}

/**
 * Wrapper class that contains the three.js model and the IK Armature object
 */
export class Actor extends Object3D {
  centroid: Object3D;

  hitbox = new Box3();
  boxHelper: Box3Helper = new Box3Helper(this.hitbox);
  disc: ActorDisc;
  nameplate?: ActorNameplate;
  nameForPlate: string = "?";

  track: ActorTrack;

  details: ActorDetails;
  positionId: number;

  transitioner: ActorTransitioner;

  private static hitboxSize = new Vector3(2.5, 7, 2.5);

  static maxAge: number = 200;

  private prevFrameNumSeen?: number;
  private prevFrameUpdated?: number;

  private poses: TaggedNeighborPair<ActorPoseCompressed | ActorPose>;

  private asset: Object3D;
  private teamColor?: Color = new Color("darkgrey");

  private boneMap: Map<number, Bone> = new Map();
  private ground = new Vector3();
  private stadiumGroundLevel: number = 0;
  private materialCache: Map<string, Material> = new Map();

  private rendererSize = new Vector2();

  private prevRoot = new Vector3();
  private nextRoot = new Vector3();

  private contextRef: Context;

  // debug
  private debug?: {
    label: TrackLabel;
  };

  constructor(context: Context, details: ActorDetails) {
    super();
    this.contextRef = context;
    this.centroid = new Object3D();

    this.details = details;
    this.positionId = context.gameState.getTrackPosition(details.uid) ?? 0;

    this.asset = this.buildModel(context);

    context.renderer.getSize(this.rendererSize);
    this.track = new ActorTrack(details.uid, this.rendererSize, 4);
    this.track.visible = context.state.actorTrailsEnabled;

    this.boxHelper.visible = false;

    this.disc = new ActorDisc(context);

    if (details.player && this.contextRef.playerFont) {
      this.nameForPlate =
        typeof details.player?.person.boxscoreName === "string"
          ? details.player.person.boxscoreName.split(", ")[0]
          : details.player.person.lastName;

      this.nameplate = new ActorNameplate(
        this.contextRef,
        this.nameForPlate,
        this.positionId,
        1.0,
        this.contextRef.playerFont,
        this.contextRef.playerFontAlt,
        this.teamColor
      );

      this.nameplate.visible = context.state.nameplatePositions.has(
        this.positionId
      );
    }

    // create debug objects if required
    if (context.state.trackingDebugEnabled) {
      this.debug = {
        label: this.makeLabel(),
      };
    }

    const initPose: ActorPoseCompressed | ActorPose = {
      uid: -1,

      rootPos: [0, -10, 0],

      packedQuats: new Uint32Array(0),
      nodeIds: new Uint16Array(),

      ground: 0,
      apex: 6,
      scale: 1,
    };

    this.add(this.asset);
    this.add(this.disc);
    this.add(this.boxHelper);
    this.add(this.centroid);
    this.add(this.track);
    if (this.nameplate) this.add(this.nameplate);

    this.transitioner = new ActorTransitioner(context);

    const boneMap = context.bufferManager.metadata?.boneIdMap;
    if (boneMap) {
      for (const idStr in boneMap) {
        const name = boneMap[idStr];
        const id = Number.parseInt(idStr);
        const bone = this.asset.getObjectByName(name);
        if (bone && bone instanceof Bone && id !== undefined) {
          this.boneMap.set(id, bone);

          if (id === 0) {
            // TODO: remove me
            bone.scale.setScalar(1 / 1.45);
          }
        } else {
          console.error(`missing bone for ${name}`);
        }
      }
    } else {
      console.error(`can't map pik to three.js asset`);
    }

    this.poses = {
      prev: { time: 0, num: 0, neighbor: initPose },
      next: { time: 0, num: 0, neighbor: initPose },
    };

    this.interpolatePose(0, 0);
  }

  update(frames: Frames, time: number) {
    // see if we've got a new frame of data
    const newFrame =
      frames.current && this.prevFrameNumSeen != frames.current.num;
    if (newFrame) {
      const neighbors = frames.getNeighbours<ActorPose | ActorPoseCompressed>(
        (frame) => {
          return frame.actorPoses.find(
            (actor) => actor.uid == this.details.uid
          );
        }
      );

      if (neighbors && neighbors.next && neighbors.prev) {
        this.poses = neighbors;
      }

      this.prevFrameNumSeen = frames.current?.num;
    }

    if (!this.poses.prev || !this.poses.next) return false;

    const prevRoot = getActorPosePos(this.poses.prev.neighbor);
    const nextRoot = getActorPosePos(this.poses.next.neighbor);

    if (!prevRoot || !nextRoot) {
      return false;
    }

    this.prevRoot.fromArray(prevRoot);
    this.nextRoot.fromArray(nextRoot);

    const interpAlpha = calculateAlpha(
      [this.poses.prev.time, this.poses.next.time],
      time
    );
    if (interpAlpha == undefined) return false;

    this.centroid.position.lerpVectors(
      this.prevRoot,
      this.nextRoot,
      interpAlpha
    );

    this.ground.x = this.centroid.position.x;
    this.ground.z = this.centroid.position.z;
    this.ground.y = lerpScalar(
      this.poses.prev.neighbor.ground,
      this.poses.next.neighbor.ground,
      interpAlpha
    );

    this.transitioner.update(this.contextRef);
    const { transY, transAlpha } = this.transitioner;

    this.stadiumGroundLevel = groundLevel(this.ground.x, this.ground.z);

    this.interpolatePose(interpAlpha, transY);

    if (this.contextRef.state.trackingDebugEnabled && !this.debug) {
      this.debug = { label: this.makeLabel() };
    }

    if (newFrame && this.contextRef.state.actorTrailsEnabled)
      this.track.update(this.centroid.position);

    let newPosition =
      this.contextRef.gameState.getTrackPosition(this.details.uid) ?? 0;
    if (newPosition != this.positionId) {
      if (this.contextRef.playerFont) {
        this.nameplate?.teardown();
        this.nameplate = new ActorNameplate(
          this.contextRef,
          this.nameForPlate,
          newPosition,
          1.0,
          this.contextRef.playerFont,
          this.contextRef.playerFontAlt,
          this.teamColor
        );
        this.add(this.nameplate);
      }
      this.positionId = newPosition;
      this.details.type = getPoserPlayerType(newPosition);
    }

    if (this.nameplate) {
      if (this.contextRef.state.nameplatePositions.has(this.positionId)) {
        this.nameplate.visible = true;
        this.nameplate.update(this.centroid.position, transAlpha);
        this.nameplate.updateTransform(this.contextRef.camera);
      } else {
        this.nameplate.visible = false;
      }
    }

    this.disc.update(this.ground, transAlpha, this.stadiumGroundLevel);

    let distFactor =
      transAlpha *
      Math.max(
        1.0,
        this.contextRef.camera.position.distanceToSquared(
          this.centroid.position
        ) / 10000
      );
    this.hitbox.setFromCenterAndSize(
      this.centroid.position,
      Actor.hitboxSize.clone().multiplyScalar(distFactor)
    );
    this.hitbox.min.y = 0;
    this.hitbox.max.y = this.poses.prev.neighbor.apex;

    // if (debug) {
    //   debug.groundSpot?.position.copy(ground);
    //   debug.armatureSkel?.update();
    //   debug.pointSkel?.setDelta(this.lerpGroundY);
    // }
    this.prevFrameUpdated = frames.current?.num;

    return true;
  }

  ageFrom(frameNum: number): number {
    return this.prevFrameUpdated !== undefined
      ? frameNum - this.prevFrameUpdated
      : Infinity;
  }

  interpolatePose(alpha: number, transitionY: number) {
    const prev = this.poses.prev?.neighbor;
    const next = this.poses.next?.neighbor;
    if (!prev || !next) return;

    if (prev instanceof ActorPose || next instanceof ActorPose) {
      if (prev instanceof ActorPose && next instanceof ActorPose) {
        return this._interpolatePoseLegacy(prev, next, alpha, transitionY);
      } else {
        console.error("interpolation whoopsie");
        return;
      }
    }

    const q = { a: new Quaternion(), b: new Quaternion() };
    const p = { a: new Vector3(), b: new Vector3() };

    const rootBone = this.boneMap.get(0);
    if (rootBone) {
      p.a.fromArray(prev.rootPos);
      p.b.fromArray(next.rootPos);
      rootBone.position.lerpVectors(p.a, p.b, alpha);
      rootBone.position.y +=
        -this.ground.y +
        this.stadiumGroundLevel +
        this.contextRef.state.groundTweak +
        transitionY;
    }

    for (let i = 0; i < prev.nodeIds.length; i++) {
      const nodeId = prev.nodeIds[i];
      const bone = this.boneMap.get(nodeId);
      // TODO: cache this??
      const qA = QuaternionPacker.unpack(prev.packedQuats[i]);
      const qBIndex = next.nodeIds.findIndex((v) => v === nodeId);

      const qB =
        qBIndex !== -1
          ? QuaternionPacker.unpack(next.packedQuats[qBIndex])
          : undefined;

      if (qA && qB && bone) {
        q.a.fromArray(qA);
        q.b.fromArray(qB);
        q.a.slerp(q.b, alpha);
        bone.rotation.setFromQuaternion(q.a);
      } else {
        console.error(`can't find bone ${nodeId}`);
      }
    }
  }

  private _interpolatePoseLegacy(
    prev: ActorPose,
    next: ActorPose,
    alpha: number,
    transitionY: number
  ) {
    const q = { a: new Quaternion(), b: new Quaternion() };
    const p = { a: new Vector3(), b: new Vector3() };

    for (const id of Object.keys(prev.poses)) {
      const poseA = prev.poses[id];
      const poseB = next.poses[id];

      const bone = this.boneMap.get(Number.parseInt(id, 10));
      if (bone && poseB) {
        if (poseA.pos && poseB.pos) {
          p.a.fromArray(poseA.pos);
          p.b.fromArray(poseB.pos);
          bone.position.lerpVectors(p.a, p.b, alpha);
          bone.position.y +=
            -this.ground.y +
            this.stadiumGroundLevel +
            this.contextRef.state.groundTweak +
            transitionY;
        }
        q.a.fromArray(poseA.quat).normalize();
        q.b.fromArray(poseB.quat).normalize();
        q.a.slerp(q.b, alpha);
        bone.rotation.setFromQuaternion(q.a);
      } else {
        console.error(`can't find bone ${id}`);
      }
    }
  }

  private buildModel(context: Context): Object3D {
    if (!context.modelTemplate) {
      console.error(`model template not set`);
      return new Group();
    }

    const model = clone(context.modelTemplate);

    model.frustumCulled = false;

    let sharedSkeleton: Skeleton | undefined = undefined;
    model.traverse((obj) => {
      obj.frustumCulled = false;

      if (obj instanceof SkinnedMesh) {
        if (!sharedSkeleton) {
          sharedSkeleton = obj.skeleton;
        } else {
          obj.bind(sharedSkeleton, obj.matrixWorld);
        }
      }
    });

    const skin = Skin.make(context, this.details);
    if (!skin) {
      console.error(
        `issue building skin for ${this.details.actorId} (${this.positionId})`
      );
    } else {
      skin.apply([model], false);
      this.teamColor = new Color(skin.material.getColors()?.primary);
    }

    return model;
  }

  private makeLabel(): TrackLabel {
    const color = Math.random() * 0xffffff;

    const label = new TrackLabel(
      `[${this.details.uid} (${this.details.actorId}-${
        this.details.player?.person.lastName ?? ""
      }) - P${this.positionId} - ${this.details.type}]`,
      // @ts-ignore
      this.contextRef.playerFont,
      color
    );
    label.scale.setScalar(0.02);
    label.position.set(2, 2, 0);
    this.centroid.add(label);

    return label;
  }

  setTransparancy(alpha: number) {
    this.asset.traverse((obj) => {
      if (obj instanceof SkinnedMesh) {
        if (!this.materialCache.get(obj.material.name)) {
          this.materialCache.set(obj.material.name, obj.material);
          obj.material = obj.material.clone();
        }
        obj.material.transparent = true;
        obj.material.opacity = alpha;
      }
    });
  }

  resetTransparancy() {
    this.asset.traverse((obj) => {
      if (obj instanceof SkinnedMesh) {
        if (this.materialCache.get(obj.material.name)) {
          obj.material.dispose();
          obj.material = this.materialCache.get(obj.material.name);
          this.materialCache.delete(obj.material.name);
        }
      }
    });
  }

  hideBehindPlate(clippingPlane = 2.5) {
    let isBatter = this.details.type == PoserPlayerType.BATTER;
    if (!isBatter && this.centroid.position.z > clippingPlane) {
      this.hide = true;
    } else {
      this.hide = false;
    }
  }

  set hide(hide: boolean) {
    this.transitioner.overrideTransition = hide ? "out" : undefined;
    this.disc.visible = !hide;
  }
}

const gltfLoader = new GLTFLoader();
export function getGltf(url: string): Promise<GLTF> {
  return gltfLoader.loadAsync(url);
}

export async function fetchActorResources(context: Context) {
  // TODO: parallelise this

  const assetBase = context.options.assetBase;
  const modelUrl = `${assetBase}/models/${context.state.modelName}`;

  const template = await getGltf(modelUrl);
  context.modelTemplate = template.scene;

  // load outfit metadata
  const outfitNames = Object.values(MannequinPlayerType);
  const promises: Promise<any>[] = outfitNames.map((name) => {
    return fetch(`${assetBase}/skins/outfits/${name}.json`)
      .then(fetchExtractJson)
      .then((data) => {
        context.outfitTemplates.set(name, data);
      })
      .catch((error) => console.error(error));
  });
  // TODO: improve this - where do we define what outfits we have?
  await Promise.all(promises);

  const materials: [string, string][] = [["umpire", ""]];

  // load materials
  context.gameState.getAwayPlayersParentTeamIds()?.forEach((teamId) => {
    if (teamId) {
      // default to LAD away for games without uniform textures
      if (teamIdToAbbrev[teamId] == undefined) {
        teamIdToAbbrev[teamId] = "LAD";
      }
      materials.push([teamIdToAbbrev[teamId], "_AWAY"]);
    } else {
      console.error(`no team id for away team`);
    }
  });
  context.gameState.getHomePlayersParentTeamIds()?.forEach((teamId) => {
    if (teamId) {
      // default to SF home for games without uniform textures
      if (teamIdToAbbrev[teamId] == undefined) {
        teamIdToAbbrev[teamId] = "SF";
      }
      materials.push([teamIdToAbbrev[teamId], "_HOME"]);
    } else {
      console.error(`no team id for away team`);
    }
  });

  return fetchMaterialsForTeam(context, materials);
}

async function fetchMaterialsForTeam(
  context: Context,
  names: [string, string][]
): Promise<any> {
  const promises: Promise<any>[] = names.map(async (name) => {
    try {
      const response = await fetch(
        `${context.options.assetBase}/skins/materials/${name.reduce(
          (p, v) => p + v,
          ""
        )}.json`
      );

      const materialData = await fetchExtractJson(response);
      if (name) {
        context.materialsTemplates.set(name[0], materialData);
      } else {
        console.error(
          "team abbreviation is null, can't grab materials template"
        );
      }
    } catch (error) {
      return console.error(error);
    }
  });
  // TODO: improve this - where do we define what materials we have?
  return Promise.all(promises);
}
